import { createAction, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  AlarmCall,
  AlarmCallParticipant,
  AlarmCallType,
  AlarmSeverity,
} from '@x-guard/xgac-types/xgac';
import {
  FetchPhoneCallsArgs,
  FetchPhoneCallsResult,
  JoinPhoneCallArgs,
  PhoneCall,
  PhoneCallsState,
  RefreshPhoneParticipantArgs,
  RS,
  SetPhoneCallMutedArgs,
} from 'types';
import { AsyncStatus } from 'types/utils';
import {
  advancedQs, api, createSlice, createThunk, isAbortError,
} from 'lib';
import { userContextSelector } from 'features/identity/auth';
import { readList } from 'features/details/chat';

const name = 'details/phoneCalls';

export const endPhoneCall = createAction<string>(`${name}/endPhoneCall`);
export const upsertPhoneParticipant = createAction<AlarmCallParticipant>(`${name}/upsertPhoneParticipant`);
export const removePhoneParticipant = createAction<string>(`${name}/removePhoneParticipant`);
const setPhoneParticipantName = createAction<{ id: string; name: string }>(`${name}/setPhoneParticipantName`);

const stateSelector = (state: RS) => state.details.phoneCalls;

const requestCode = async (call: AlarmCall, muted: boolean, signal?: AbortSignal): Promise<PhoneCall> => {

  const { data } = await api.post<{ code: string; muted: boolean }>(
    `/alarm-calls/${call._id}/phone-join`,
    { muted },
    { signal },
  );

  return { call, code: data.code, muted: data.muted };

};

const readParticipants = (callId: string, signal: AbortSignal) => readList<AlarmCallParticipant>(
  `/alarm-calls/${callId}/alarm-call-participants?${advancedQs({ $max: 200 })}`,
  signal,
);

/**
 * Request the join code and load the participants. phone-join also applies `muted` to a live leg, so when you are
 * already connected (page reload) keep the mute state you have instead of resetting it to the default.
 * The participants are read first and a failed read fails the join: guessing could unmute an open microphone.
 */
const joinCall = async (call: AlarmCall, defaultMuted: boolean, userId: string | undefined, signal: AbortSignal) => {

  const participants = await readParticipants(call._id, signal);
  const live = participants.find(({ ref, state }) => (
    ref?._ref === 'User' && ref._id === userId && !!state.connectedAt && !state.disconnectedAt
  ));

  return { phoneCall: await requestCode(call, live ? live.state.muted : defaultMuted, signal), participants };

};

export const fetchPhoneCalls = createThunk<FetchPhoneCallsResult, FetchPhoneCallsArgs>({
  name,
  handler: async ({ alarmId, startMuted, controller: { signal } }, { getState }) => {

    const openCalls = (await readList<AlarmCall>(`/alarms/${alarmId}/calls`, signal)).filter((call) => !call.endedAt);
    const userId = userContextSelector(getState() as RS)?.user?._id;

    const joined = await Promise.all(openCalls.map(async (call) => {

      try {

        return await joinCall(call, startMuted && call.type === AlarmCallType.AlarmCall, userId, signal);

      } catch (error) {

        // 409: the call ended in the meantime. Anything else is a real failure and must not look like "no call".
        if (axios.isAxiosError(error) && error.response?.status === 409) return null;

        throw error;

      }

    }));
    const results = joined.filter((result): result is NonNullable<typeof result> => result !== null);

    return { calls: results.map(({ phoneCall }) => phoneCall), participants: results.flatMap(({ participants }) => participants) };

  },
});

/** An open call showed up (or changed) over the websocket: request a code and load its participants unless we already have the call. */
export const joinPhoneCall = createThunk<FetchPhoneCallsResult, JoinPhoneCallArgs>({
  name: `${name}/join`,
  handler: async ({ call, controller: { signal } }, { getState }) => {

    const state = getState() as RS;

    // Alarm not loaded yet (or already replaced): mute-on-join is unknown, and fetchPhoneCalls picks the call up.
    const alarm = state.details.value?.alarm;

    const { calls, endedCallIds } = stateSelector(state);

    if (!alarm || endedCallIds.includes(call._id) || calls.some((phoneCall) => phoneCall.call._id === call._id)) return { calls: [], participants: [] };

    // Red alarms start muted, same as the legacy dynamic conference call. The helper call never does.
    const { phoneCall, participants } = await joinCall(
      call,
      call.type === AlarmCallType.AlarmCall && alarm.severity === AlarmSeverity.Red,
      userContextSelector(state)?.user?._id,
      signal,
    );

    return { calls: [phoneCall], participants };

  },
});

export const setPhoneCallMuted = createThunk<void, SetPhoneCallMutedArgs>({
  name: `${name}/setMuted`,
  handler: async ({ alarmCallId, muted }) => {

    await api.post(`/alarm-calls/${alarmCallId}/phone-join`, { muted });

  },
});

/** Websocket payloads hide the operator name; fetch the participant over REST to get it when we are allowed to see it. */
export const refreshPhoneParticipant = createThunk<void, RefreshPhoneParticipantArgs>({
  name: `${name}/refreshParticipant`,
  handler: async ({ alarmCallId, participantId }, { dispatch }) => {

    const [participant] = await readList<AlarmCallParticipant>(
      `/alarm-calls/${alarmCallId}/alarm-call-participants?${advancedQs({ _id: participantId, $max: 1 })}`,
      new AbortController().signal,
    );

    if (participant?.name) dispatch(setPhoneParticipantName({ id: participant._id, name: participant.name }));

  },
});

/**
 * Add what a snapshot found without discarding newer websocket state: calls that ended while the request was
 * in flight stay gone, and known calls/participants are kept as they are.
 */
const mergeResult = (state: PhoneCallsState, { calls, participants }: FetchPhoneCallsResult) => {

  const added = calls.filter(({ call }) => !state.endedCallIds.includes(call._id) && !state.calls.some((known) => known.call._id === call._id));

  state.calls.push(...added);
  state.participants.push(...participants.filter((participant) => (
    !state.endedCallIds.includes(participant.alarmCall._id)
    && !state.participants.some(({ _id }) => _id === participant._id)
  )));

};

const setMuted = (state: PhoneCallsState, { alarmCallId, participantId }: SetPhoneCallMutedArgs, muted: boolean) => {

  const phoneCall = state.calls.find(({ call }) => call._id === alarmCallId);
  const participant = state.participants.find(({ _id }) => _id === participantId);

  if (phoneCall) phoneCall.muted = muted;

  if (participant) participant.state.muted = muted;

};

export const phoneCalls = createSlice<PhoneCallsState, typeof name>({
  name,
  initialState: {
    status: AsyncStatus.Idle,
    calls: [],
    participants: [],
    endedCallIds: [],
  },
  extraReducers: (builder) => builder
    .addCase(fetchPhoneCalls.pending, (state) => {

      if (state.status.idle) state.status = AsyncStatus.Pending;

    })
    .addCase(fetchPhoneCalls.fulfilled, (state, action) => {

      if (action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Fulfilled;
      mergeResult(state, action.payload);

    })
    .addCase(fetchPhoneCalls.rejected, (state, action) => {

      if (isAbortError(action.error) || action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Rejected;

    })
    .addCase(joinPhoneCall.fulfilled, (state, action) => {

      if (!action.meta.arg.controller.signal.aborted) mergeResult(state, action.payload);

    })
    .addCase(setPhoneCallMuted.pending, (state, action) => setMuted(state, action.meta.arg, action.meta.arg.muted))
    .addCase(setPhoneCallMuted.rejected, (state, action) => setMuted(state, action.meta.arg, !action.meta.arg.muted))
    .addCase(endPhoneCall, (state, action) => {

      state.endedCallIds.push(action.payload);
      state.calls = state.calls.filter(({ call }) => call._id !== action.payload);
      state.participants = state.participants.filter((participant) => participant.alarmCall._id !== action.payload);

    })
    .addCase(upsertPhoneParticipant, (state, action) => {

      const index = state.participants.findIndex((participant) => participant._id === action.payload._id);

      if (index === -1) {

        state.participants.push(action.payload);
        return;

      }

      // Keep a name we already know when the websocket sends the redacted version.
      const knownName = state.participants[index].name;

      state.participants[index] = {
        ...action.payload,
        name: action.payload.name || knownName,
      };

    })
    .addCase(setPhoneParticipantName, (state, action) => {

      const participant = state.participants.find(({ _id }) => _id === action.payload.id);

      if (participant) participant.name = action.payload.name;

    })
    .addCase(removePhoneParticipant, (state, action) => {

      state.participants = state.participants.filter((participant) => participant._id !== action.payload);

    }),
});

export const phoneCallsStatusSelector = (state: RS) => stateSelector(state).status;

/** Open calls we can join by phone, the alarm call first. */
export const phoneCallsSelector = createSelector(
  (state: RS) => stateSelector(state).calls,
  (calls) => [...calls].sort((a, b) => Number(b.call.type === AlarmCallType.AlarmCall) - Number(a.call.type === AlarmCallType.AlarmCall)),
);

export const openAlarmCallIdsSelector = createSelector(
  (state: RS) => stateSelector(state).calls,
  (calls) => calls.filter(({ call }) => call.type === AlarmCallType.AlarmCall).map(({ call }) => call._id).join(','),
);

export const phoneCallParticipantsSelector = (alarmCallId: string) => createSelector(
  (state: RS) => stateSelector(state).participants,
  (state: RS) => userContextSelector(state)?.user?._id,
  (participants, userId) => participants
    .filter((participant) => participant.alarmCall._id === alarmCallId)
    .map((participant) => ({
      participant,
      isSelf: participant.ref?._ref === 'User' && participant.ref._id === userId,
      inCall: !!participant.state.connectedAt && !participant.state.disconnectedAt,
    })),
);
